import { ApiError } from "./api";

export interface PinSignInOrganization {
  id: string;
  name: string;
}

export interface PinSignInSite {
  id: string;
  name: string;
}

export interface PinSignInMember {
  id: string;
  display_name: string;
}

export interface PinSession {
  member_id: string;
  display_name: string;
  access_token: string;
  expires_at: string;
}

/**
 * The public, unauthenticated PIN sign-in surface — reached from a
 * bookmarked `/o/<org-code>` link, before there is any access token to
 * attach. Kept separate from KitchenHubApi, which always requires one.
 */
export class PinSignInApi {
  constructor(private readonly baseUrl: string) {}

  resolveOrganization(code: string): Promise<PinSignInOrganization> {
    return this.request(`/v1/pin-sign-in/organizations/${encodeURIComponent(code)}`);
  }

  resolveSite(organizationId: string, code: string): Promise<PinSignInSite> {
    return this.request(`/v1/pin-sign-in/organizations/${organizationId}/sites/${encodeURIComponent(code)}`);
  }

  listMembers(organizationId: string, siteId?: string | null): Promise<PinSignInMember[]> {
    const query = siteId ? `?${new URLSearchParams({ site_id: siteId })}` : "";
    return this.request(`/v1/pin-sign-in/organizations/${organizationId}/members${query}`);
  }

  signIn(organizationId: string, memberId: string, pin: string): Promise<PinSession> {
    return this.request(`/v1/pin-sign-in/organizations/${organizationId}/sign-in`, {
      method: "POST",
      body: JSON.stringify({ member_id: memberId, pin }),
    });
  }

  refresh(token: string): Promise<PinSession> {
    return this.request("/v1/pin-sign-in/refresh", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...init.headers },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { detail?: string } | null;
      throw new ApiError(
        body?.detail ?? "Kitchen Hub could not complete the request.",
        response.status,
        response.headers.get("x-request-id"),
      );
    }
    return (await response.json()) as T;
  }
}
